import { create } from 'zustand';
import type { UserSession } from 'types';
import type { SclaApplication } from 'features/scla/types';
import type { IUpdateProfileForm } from 'features/profile/hooks/useUpdateProfile';
import chooseSclaApplication from 'features/scla/utils/chooseSclaApplication';
import normalizeUserSession from 'utils/normalizeUserSession';
import storage from 'utils/storage';

export type UserSessionStoreState = {
  user: UserSession | null;
  storedEmail: string;
  application: SclaApplication | null;
  showConfetti: boolean;
  setStoredEmail: (email: string) => void;
  clearStoredEmail: () => void;
  setUser: (user: UserSession) => void;
  logout: () => void;
  setUserApplication: (app: SclaApplication) => void;
  setUserProfileImage: (url: string) => void;
  setUserProfile: (data: IUpdateProfileForm) => void;
  updateShowConfetti: (show: boolean) => void;
};

const initialStoredEmail = storage.getLoginEmail() || '';

const useUserStore = create<UserSessionStoreState>(set => ({
  user: null,
  storedEmail: initialStoredEmail,
  application: null,
  showConfetti: true,
  setStoredEmail: (email: string) => {
    if (email) {
      storage.setLoginEmail(email);
      set({ storedEmail: email });
    }
  },
  clearStoredEmail: () => {
    storage.clearLoginEmail();
    set({ storedEmail: '' });
  },
  setUser: (user: UserSession) => {
    if (user) {
      const nextUser = normalizeUserSession(user);
      set(state => ({
        user: nextUser,
        application: state.user?._id === nextUser._id ? state.application : null,
      }));
    }
  },
  logout: () => set({ user: null, application: null }),
  setUserApplication: (app: SclaApplication) => set(state => {
    const next = chooseSclaApplication(state.application, app, state.user?._id);
    if (!next || next === state.application) return state;
    return { application: next };
  }),
  setUserProfileImage: (url: string) => {
    set((state: UserSessionStoreState) => {
      if (!state.user) return state;
      return { user: { ...state.user, profileImage: url } };
    });
  },
  setUserProfile: (data: IUpdateProfileForm) => {
    set((state: UserSessionStoreState) => {
      if (!state.user) return state;
      return { user: { ...state.user, ...data } };
    });
  },
  updateShowConfetti: (show: boolean) => set({ showConfetti: show }),
}));

export default useUserStore;
